/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MIconMenu } from 'library';
import SubdirectoryArrowLeftRoundedIcon from '@mui/icons-material/SubdirectoryArrowLeftRounded';

import { useUserRolePermissions } from 'hooks';
import { bool, func, number, string } from 'prop-types';
import { format } from 'date-fns';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import * as R from 'ramda';
import { AuthContext } from 'authentication/AuthContext';
import { isEditableStatus, shipmentStatusTooltips } from './utils';
import StatusChip from './components/StatusChip';

const titleStyling = {
  fontWeight: 400,
};

const gridStyle = {
  display: 'grid',
  gridGap: 8,
  gridTemplateAreas: {
    xs: `
      'main action'
      'acceptance volume'
      'origin date'
      'product agreementCode'
      `,
    lg: `'main hr status acceptance origin product volume bookedVolume agreementCode date action'`,
  },
  gridTemplateColumns: {
    xs: '1fr 1fr',
    lg: '1.25fr 0.2fr repeat(7, 1fr)',
  },
  gridTemplateRows: { xs: '1fr', lg: '1fr' },
};

const ShipmentsCard = ({
  acceptance,
  batchOwnerName,
  fromPage,
  onCancel,
  isOnlyShipment,
  isParentBatchView,
  originName,
  parentBatchId,
  requestedDate,
  shipmentId,
  shipperName,
  status,
  volume,
  bookedVolume,
  agreementCode,
  productCode,
}) => {
  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);
  const navigate = useNavigate();

  const shipperView = [
    {
      label: 'View Shipment',
      onClick: () =>
        navigate(`../batches/shipment/${shipmentId}/edit`, { state: { from: fromPage } }),
      value: 'view',
    },
  ];

  const batchOwnerView = [
    {
      label: 'View Parent Batch',
      onClick: () => navigate(`../batches/batch/${parentBatchId}/edit`),
      value: 'viewParent',
    },
  ];

  const isBatchOwner = affiliates.some(({ customerNameShort }) =>
    R.equals(customerNameShort, batchOwnerName)
  );

  const viewMenu =
    isBatchOwner && !isParentBatchView ? R.concat(shipperView, batchOwnerView) : shipperView;

  const manageMenu = isBatchOwner
    ? [
        {
          label: 'View Parent Batch',
          onClick: () => navigate(`../batches/batch/${parentBatchId}/edit`),
          value: 'viewParent',
        },
        {
          label: 'Edit Shipment',
          onClick: () =>
            navigate(`../batches/shipment/${shipmentId}/edit`, { state: { from: fromPage } }),
          value: 'edit',
        },
        {
          label: 'Cancel Shipment',
          onClick: () => {
            onCancel('shipment', shipmentId, isOnlyShipment);
          },
          value: 'cancel',
        },
      ]
    : [
        {
          label: 'Edit Shipment',
          onClick: () =>
            navigate(`../batches/shipment/${shipmentId}/edit`, { state: { from: fromPage } }),
          value: 'edit',
        },
      ];

  const menuItems = R.and(isProductShippingManage, isEditableStatus(status))
    ? manageMenu
    : viewMenu;

  return (
    <Card sx={{ '&:last-child': { mb: 0 }, mb: 2 }}>
      <CardContent
        sx={{
          p: { xs: 2, lg: 3 },
        }}
      >
        <Box sx={gridStyle}>
          <Stack spacing={0.5} sx={{ gridArea: 'main', gridColumn: 1, mb: { xs: 1, lg: 0 } }}>
            <Typography sx={titleStyling} variant="caption">
              #{shipmentId}
            </Typography>
            <Typography sx={{ fontWeight: 700 }} variant="body1">
              {shipperName}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <SubdirectoryArrowLeftRoundedIcon
                sx={{ color: 'graphic.dark', fontSize: 16, transform: 'rotate(90deg)' }}
              />
              <Typography sx={{ fontWeight: 400, pt: '2px' }} variant="caption">
                {batchOwnerName}
              </Typography>
            </Stack>
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
              <Tooltip
                enterTouchDelay={10}
                placement="top"
                title={shipmentStatusTooltips[status] || ''}
              >
                <StatusChip status={status} />
              </Tooltip>
            </Box>
          </Stack>
          <Box
            sx={{
              borderLeft: 1,
              borderLeftColor: 'graphic.light',
              display: { xs: 'none', lg: 'block' },
              gridArea: 'hr',
              gridColumn: 2,
            }}
          />
          <Stack spacing={0.5} sx={{ display: { xs: 'none', lg: 'flex' }, gridArea: 'status' }}>
            <Typography
              sx={{ ...titleStyling, display: { xs: 'none', lg: 'inline' } }}
              variant="caption"
            >
              Status
            </Typography>
            <Tooltip
              enterTouchDelay={10}
              placement="top"
              title={shipmentStatusTooltips[status] || ''}
            >
              <StatusChip status={status} />
            </Tooltip>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'acceptance' }}>
            <Typography sx={titleStyling} variant="caption">
              Acceptance
            </Typography>
            <Typography variant="body2">{acceptance}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'origin' }}>
            <Typography sx={titleStyling} variant="caption">
              Origin
            </Typography>
            <Typography variant="body2">{originName}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'product' }}>
            <Typography sx={titleStyling} variant="caption">
              Product
            </Typography>
            <Typography variant="body2">{productCode}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'volume' }}>
            <Typography sx={titleStyling} variant="caption">
              Volume
            </Typography>
            <Typography variant="body2">{formatNumberWithoutDecimals(volume)} bbl</Typography>
          </Stack>

          <Stack
            spacing={0.5}
            sx={{ display: { xs: 'none', lg: 'flex' }, gridArea: 'bookedVolume' }}
          >
            <Typography sx={titleStyling} variant="caption">
              Booked Volume
            </Typography>
            <Typography variant="body2">{bookedVolume ?? '--'}</Typography>
          </Stack>

          <Stack spacing={0.5} sx={{ gridArea: 'agreementCode' }}>
            <Typography sx={titleStyling} variant="caption">
              Agreement Code
            </Typography>
            <Typography variant="body2">{agreementCode}</Typography>
          </Stack>

          <Stack spacing={0.5} sx={{ gridArea: 'date' }}>
            <Typography sx={titleStyling} variant="caption">
              Requested Date
            </Typography>
            <Typography variant="body2">
              {format(Date.parse(requestedDate), 'MMMM d, yyyy')}
            </Typography>
          </Stack>
          {menuItems.length > 0 ? (
            <Box sx={{ gridArea: 'action', justifySelf: 'end' }}>
              <MIconMenu items={menuItems} />
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

ShipmentsCard.propTypes = {
  acceptance: string.isRequired,
  batchOwnerName: string,
  fromPage: string.isRequired,
  onCancel: func.isRequired,
  isOnlyShipment: bool.isRequired,
  isParentBatchView: bool.isRequired,
  originName: string.isRequired,
  parentBatchId: number,
  requestedDate: string.isRequired,
  shipmentId: number.isRequired,
  shipperName: string.isRequired,
  status: string.isRequired,
  volume: number.isRequired,
  bookedVolume: number.isRequired,
  agreementCode: string.isRequired,
  productCode: string.isRequired,
};

ShipmentsCard.defaultProps = {
  batchOwnerName: '',
  parentBatchId: null,
};

export default ShipmentsCard;
